import { useState } from "react";
import { Download, Upload, AlertTriangle, CheckCircle2, FileText, Loader2, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function BackupPanel() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [reseedingAllievi, setReseedingAllievi] = useState(false);
  const [replace, setReplace] = useState(false);
  const [lastReport, setLastReport] = useState(null);

  const doReseedPenal = async () => {
    if (!confirm("Confermi il ripristino del Codice Penale dal file di seed?\n\n• Verranno aggiunti gli articoli mancanti\n• Quelli esistenti verranno aggiornati con la versione del file\n• Operazione sicura (idempotente)")) return;
    setReseeding(true);
    try {
      const { data } = await api.post("/penal-code/admin/reseed");
      toast.success(data?.message || "Codice penale ripristinato");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore ripristino codice penale");
    } finally { setReseeding(false); }
  };

  const doReseedAllievi = async () => {
    if (!confirm("ATTENZIONE: Sostituirai TUTTE le domande del tier Allievi (Vice Sceriffo) con quelle del file di seed integrato.\n\n• Le 24 domande ufficiali verranno reinserite\n• Verranno eliminate solo le domande Vice Sceriffo (i tier Sergente/Tenente/Capitano restano intatti)\n• I tentativi degli allievi nello storico restano intatti\n\nConfermi?")) return;
    setReseedingAllievi(true);
    try {
      const { data } = await api.post("/quiz/admin/reseed-allievi");
      toast.success(data?.message || "Domande Allievi ripristinate");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore ripristino domande Allievi");
    } finally { setReseedingAllievi(false); }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `lssd-backup-${date}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch {
      toast.error("Errore durante l'export");
    } finally { setExporting(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    if (replace && !confirm("ATTENZIONE: SVUOTERAI il database e lo sostituirai col backup. Continuare?")) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const payload = { version: parsed.version || 1, data: parsed.data || {}, replace };
      const { data } = await api.post("/backup/import", payload);
      setLastReport(data.report);
      toast.success("Backup importato");
    } catch (e) {
      toast.error("Errore import: " + (e?.message || "file non valido"));
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="backup-panel">
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-xl text-[#F5E6B8] mb-1">Esporta Backup</h3>
        <p className="text-sm text-[#D4C480] mb-4">Scarica un file JSON con tutti gli utenti, news, candidature, settings e codice penale. Conserva il file in luogo sicuro.</p>
        <Button onClick={doExport} disabled={exporting} data-testid="backup-export-btn" className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold">
          <Download className="h-4 w-4 mr-2" /> {exporting ? "Esportazione…" : "Scarica Backup"}
        </Button>
      </div>

      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-xl text-[#F5E6B8] mb-1">Ripristina Backup</h3>
        <p className="text-sm text-[#D4C480] mb-4">Carica un file JSON precedentemente esportato. In modalità <span className="text-sheriff-gold font-bold">Merge</span> i duplicati verranno saltati. In modalità <span className="text-rose-300 font-bold">Replace</span> il DB verrà svuotato e ricreato.</p>
        <label className="flex items-start gap-3 cursor-pointer p-4 border border-sheriff-gold/20 rounded-xl bg-[#0A1F18] mb-4">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="backup-replace" className="mt-1" />
          <div>
            <div className="font-semibold text-[#F5E6B8] text-sm">Modalità Replace (rischiosa)</div>
            <div className="text-xs text-[#B8A269] mt-0.5">Svuota completamente le collezioni prima di reimportare. Usa solo dopo aver verificato il file.</div>
          </div>
        </label>
        <input
          type="file"
          accept="application/json,.json"
          onChange={(e) => doImport(e.target.files?.[0])}
          disabled={importing}
          data-testid="backup-import-file"
          className="text-sm file:mr-3 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-sheriff-gold file:text-[#0A1F18] file:font-bold file:uppercase file:tracking-[0.15em] file:cursor-pointer cursor-pointer"
        />
        {importing && <p className="text-sm text-sheriff-gold mt-3">Importazione in corso…</p>}
      </div>

      {lastReport && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-6" data-testid="backup-report">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-300" />
            <h3 className="font-heading font-bold text-emerald-300">Report Importazione</h3>
          </div>
          <div className="space-y-2 text-sm">
            {Object.entries(lastReport).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 font-mono text-[#E8D89A]">
                <span className="uppercase">{k}</span>
                <span><span className="text-emerald-300">{v.imported} importati</span> · <span className="text-amber-300">{v.skipped} saltati</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-rose-500/8 border border-rose-500/30 rounded-2xl p-5">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-rose-300 shrink-0 mt-0.5" />
          <div className="text-sm text-[#D4C480]">
            <span className="font-bold text-rose-300">Consiglio:</span> esegui un export ogni volta che pubblichi news importanti o dopo aver approvato batch di candidature. Il file di backup è la tua unica polizza contro la perdita dei dati.
          </div>
        </div>
      </div>

      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6">
        <div className="flex items-start gap-3 mb-3">
          <FileText className="h-6 w-6 text-sheriff-gold shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading font-bold text-xl text-[#F5E6B8] mb-1">Ripristina Codice Penale</h3>
            <p className="text-sm text-[#D4C480]">
              Ripopola tutti i <span className="font-bold text-sheriff-gold">148 articoli</span> del Codice Penale dal file di seed integrato nel server.
              Utile se dopo un redeploy la sezione risulta vuota. Operazione sicura: aggiunge solo gli articoli mancanti e aggiorna quelli esistenti.
            </p>
          </div>
        </div>
        <Button
          onClick={doReseedPenal}
          disabled={reseeding}
          data-testid="reseed-penal-btn"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase-label"
        >
          {reseeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
          Ripristina Codice Penale
        </Button>
      </div>

      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6">
        <div className="flex items-start gap-3 mb-3">
          <GraduationCap className="h-6 w-6 text-sheriff-gold shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading font-bold text-xl text-[#F5E6B8] mb-1">Ripristina Domande Allievi</h3>
            <p className="text-sm text-[#D4C480]">
              Ripristina le <span className="font-bold text-sheriff-gold">24 domande ufficiali</span> dell&apos;esame Allievi (tier Vice Sceriffo) dal file di seed integrato nel server.
              ⚠️ <span className="font-bold text-amber-300">Sostituisce</span> completamente le domande Vice Sceriffo esistenti (i tier Sergente/Tenente/Capitano restano intatti).
            </p>
          </div>
        </div>
        <Button
          onClick={doReseedAllievi}
          disabled={reseedingAllievi}
          data-testid="reseed-allievi-btn"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase-label"
        >
          {reseedingAllievi ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GraduationCap className="h-4 w-4 mr-2" />}
          Ripristina Domande Allievi
        </Button>
      </div>
    </div>
  );
}
